import React from "react";
import PropTypes from "prop-types";
// Material imports
import DialogContentText from "@mui/material/DialogContentText";
// Limitless imports
import Grid from "@limitless/modern/layout/Grid";
import Paper from "@limitless/modern/surfaces/paper/Paper";
import Typography from "@limitless/modern/data-display/typography/Typography";
import Dialog from "@limitless/modern/feedback/dialog/Dialog";
import Button from "@limitless/modern/inputs/button/Button";
import IconButton from "@limitless/modern/inputs/icon-button/IconButton";
import { useExpertPaymentsContext, useExpertProfileContext } from "@limitless/expert";
import { useAppConfig } from "@limitless/common/hooks";
import { POLICY_WITHDRAWAL_ACCESS } from "@limitless/portal";

// Limitless Icons
import WithdrawalInfo from "@limitless/icons/InfoCircle";

const AvailableToWithdraw = ({ navigate, theme }) => {
    const [withdrawlInfoOpen, setWithdrawlInfoOpen] = React.useState(false);

    const { formatCurrency, profile, canUserAccess } = useExpertProfileContext();
    const { getMinWithdrawalAmount, getContactUsUrl } = useAppConfig();
    const minWithdrawalAmount = getMinWithdrawalAmount(profile.balanceCurrency);
    const userCanWithdraw = canUserAccess(POLICY_WITHDRAWAL_ACCESS);

    const { payments, requestWithdrawal } = useExpertPaymentsContext();
    const { loading } = payments;

    const renderBalance = React.useMemo(() => {
        const { balance, country, language, userName, balanceCurrency, balanceCurrencySymbol } = profile;

        return formatCurrency(balance, { balanceCurrency, balanceCurrencySymbol, country, language, userName });
    }, [profile]);

    const handleWithdrawlInfoToggle = () => {
        setWithdrawlInfoOpen(!withdrawlInfoOpen);
    };

    return (
        <>
            <Paper
                sx={{
                    maxWidth: "unset",
                    minHeight: "auto"
                }}
                elevation={2}
            >
                <Grid container sx={{ padding: theme => theme.spacing(3) }} display="flex" flexDirection="row">
                    <Grid item xs={12} sm={12} md={12} lg={6}>
                        <Grid
                            item
                            container
                            display="flex"
                            flexDirection="column"
                            alignItems="flex-start"
                            sx={{ rowGap: theme => theme.spacing(1) }}
                        >
                            <Grid item variant="subtitle1" color="text.primary" component={Typography}>
                                Available to withdraw
                            </Grid>
                            <Grid display="flex" flexDirection="row" justifyContent="space-between" alignItems="end"> 
                                <Grid item component={Typography} variant="h4" color="text.primary">
                                    {renderBalance}{" "}
                                </Grid>
                                <Grid
                                    item
                                    component={Typography}
                                    variant="caption"
                                    sx={{ marginLeft: 1, paddingBottom: 1 }}
                                    color="text.secondary"
                                >
                                    before pending
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                {userCanWithdraw && (
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        id="requestWithdrawal"
                                        onClick={requestWithdrawal}
                                        disabled={
                                            profile.balance < minWithdrawalAmount || profile.balance === 0 || loading
                                        }
                                    >
                                        Withdraw
                                    </Button>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={6}>
                        <Grid
                            container
                            alignItems="flex-end"
                            sx={{ textAlign: "right", rowGap: theme => theme.spacing(1.25) }}
                            justifyContent="flex-end"
                        >
                            <Grid item xs={12}>
                                <IconButton
                                    icon={<WithdrawalInfo />}
                                    label="Withdrawal process"
                                    onClick={() => handleWithdrawlInfoToggle(true)}
                                    sx={{ marginTop: "0px", marginBottom: 1.2 }}
                                    id="widthdrawalProcessButton"
                                />
                            </Grid>
                            <Grid item component={Typography} variant="caption" color="text.secondary">
                                Your Paypal email address
                            </Grid>
                            <Grid
                                item
                                component={Typography}
                                variant="body2"
                                xs={10}
                                sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    display: "block",
                                    whiteSpace: "nowrap"
                                }}
                                color="text.primary"
                            >
                                {profile.paypalAccount || "Not set"}
                            </Grid>
                            <Grid
                                component={Typography}
                                variant="textButton"
                                color="primary.main"
                                onClick={() => navigate("/settings/2")}
                                sx={{ cursor: "pointer" }}
                                id="updatePayPalButton"
                            >
                                Click to update
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Paper>
            <Dialog
                open={withdrawlInfoOpen}
                onClose={handleWithdrawlInfoToggle}
                aria-labelledby="withdrawl-process"
                id="withdrawl-process"
                title="Withdrawal Process"
                onOkClick={() => handleWithdrawlInfoToggle(false)}
                okText="Close"
                paperStyle={{ maxWidth: "580px" }}
            >
                <DialogContentText>
                    <Typography variant="subtitle2" color="primary.text">
                        It is important that your PayPal email address is entered correctly in your profile. Withdrawals
                        are subject to a 1.96% PayPal fee.
                    </Typography>
                </DialogContentText>
                <DialogContentText>
                    <Typography variant="body2" sx={{ display: "inline" }}>
                        {"You can make one withdrawal whenever your balance is"}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ fontWeight: "bold", display: "inline" }}
                        translationSubstitutes={minWithdrawalAmount}
                        ml={0.5}
                    >
                        {"over {0}."}
                    </Typography>
                    <Typography variant="body2" sx={{ display: "inline" }} ml={0.5}>
                        {"Your withdrawal request will be processed within"}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold", display: "inline" }} ml={0.5}>
                        {"5 working days"}
                    </Typography>
                </DialogContentText>
                <DialogContentText>
                    <Typography
                        variant="body2"
                        style={{
                            display: "inline"
                        }}
                    >
                        For failed withdrawals, please{" "}
                    </Typography>
                    <Typography
                        style={{
                            color: theme.palette.primary.main,
                            cursor: "pointer",
                            display: "inline"
                        }}
                        id="contactUs"
                        color="primary.main"
                        variant="body2"
                        onClick={() => {
                            const contactUsPath = "/contactusscreen";
                            const url = getContactUsUrl(contactUsPath);
                            if (url.includes(contactUsPath)) {
                                navigate(url);
                            } else {
                                window.open(url);
                            }
                        }}
                    >
                        contact us
                    </Typography>
                    <Typography
                        variant="body2"
                        style={{
                            display: "inline"
                        }}
                    >
                        .
                    </Typography>
                </DialogContentText>
            </Dialog>
        </>
    );
};

AvailableToWithdraw.propTypes = {
    navigate: PropTypes.func.isRequired,
    theme: PropTypes.object.isRequired
};

export default AvailableToWithdraw;
